<template>
  <FormMainContainer
    :title="title"
    :previous-step-route="previousStepRoute"
    @previousStepClick="resetValidation()"
  >
    <div class="submission-description" v-html="$t('submission_form.description')" />
    <ContactForm class="mb-4" />
    <p v-if="loading" class="f-description mb-4">
      {{ $t('submission_form.submitting') }}
    </p>
    <p>
      <span v-if="submissionError" class="fh2">
        {{ $t('submission_form.submission_error') }}
      </span>
    </p>
    <p v-if="submissionError" class="text-alert">
      {{ $t('submission_form.submission_error') }}
    </p>
    <p v-if="reCaptchaError" class="text-alert">
      {{ $t('submission_form.reCaptcha_error') }}
    </p>
    <div class="f-submit pt-1">
      <button
        v-if="hasTheAbilityToGoBackToSelection"
        id="appointment-submit-btn"
        class="o-btn-action me-3"
        type="submit"
        :disabled="loading"
        href="#"
        :aria-label="$t('submission_form.go_back_to_selection')"
        @click="goBackToSelection()"
      >
        <span>{{ $t('submission_form.go_back_to_selection') }}</span>
      </button>
      <button
        v-else
        ref="button"
        id="appointment-submit-btn"
        class="o-btn-action"
        type="submit"
        :disabled="loading || !isContactFormValid"
        href="#"
        :aria-label="$t('submission_form.submit_aria_label')"
        @click="onSubmit()"
      >
        <span>{{ $t('submission_form.submit_btn') }}</span>
      </button>
    </div>
  </FormMainContainer>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import FormMainContainer from '~concierge/components/FormMainContainer.vue'
import HeaderSection from '~concierge/components/HeaderSection.vue'
import FooterSection from '~concierge/components/FooterSection.vue'
import ContactForm from '~conciergeUiCommons/components/ContactForm.vue'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'
import { router, usePage } from '@inertiajs/vue3'
import { wTrans } from 'laravel-vue-i18n'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'

export default defineComponent({
  components: { HeaderSection, FooterSection, ContactForm, FormMainContainer },
  props: {
    submitFormRoute: {
      type: String,
      required: true
    },
    appointmentFormRoutes: {
      type: Object,
      required: true
    }
  },
  setup() {
    const appointmentStore = useAppointmentStore()
    if (!appointmentStore.isAppointmentConfirmed) {
      const page = usePage()
      const homeUrl = page.props.homeUrl as string
      return router.visit(homeUrl)
    }
    const submissionFormStore = useSubmissionFormStore()
    const submissionError = ref<boolean>(false)
    const hasBeenSubmittedOnce = ref<boolean>(false)
    const reCaptcha = useReCaptcha()
    const reCaptchaError = ref<boolean>(false)
    return {
      hasBeenSubmittedOnce,
      reCaptcha,
      submissionFormStore,
      appointmentStore,
      submissionError,
      reCaptchaError
    }
  },
  computed: {
    hasTheAbilityToGoBackToSelection(): boolean {
      return this.submissionFormStore.hasTheAbilityToGoBackToSelection
    },
    title(): string {
      return wTrans('submission_form.title').value
    },
    isContactFormValid(): boolean {
      return this.submissionFormStore.isFormValid
    },
    loading(): boolean {
      return this.submissionFormStore.formData.processing
    },
    appointmentType(): ConciergeAppointmentQuestionFlow | null {
      return this.appointmentStore.appointment.flow
    },
    previousStepRoute(): string {
      if (
        this.hasBeenSubmittedOnce &&
        this.submissionFormStore.isFormValid &&
        this.appointmentType !== null
      ) {
        switch (this.appointmentType) {
          case ConciergeAppointmentQuestionFlow.TestDriveAppointment:
            return this.appointmentFormRoutes.test_drive_appointment
          case ConciergeAppointmentQuestionFlow.FreeAppointment:
            return this.appointmentFormRoutes.free_appointment
          case ConciergeAppointmentQuestionFlow.AdviserSelectionAppointment:
            return this.appointmentFormRoutes.adviser_appointment
        }
      }
      return ''
    }
  },
  methods: {
    resetValidation(): void {
      this.submissionFormStore.resetValidationState()
      this.reCaptchaError = false
    },
    async onSubmit(): Promise<void> {
      this.reCaptchaError = false
      if (this.submitFormRoute.length > 0) {
        try {
          await this.reCaptcha?.recaptchaLoaded()
          const token = await this.reCaptcha?.executeRecaptcha('submitConciergeForm')
          if (token && typeof token == 'string') {
            this.submissionFormStore.formData.captcha_token = token
          } else {
            console.error('Unable to get reCAPTCHA token')
            this.reCaptchaError = true
            return
          }
          this.hasBeenSubmittedOnce = true
          await this.submissionFormStore.submitForm(this.submitFormRoute)
        } catch (error: unknown | Error) {
          console.error(error)
          this.submissionError = true
        }
      }
    },
    goBackToSelection(): void {
      this.resetValidation()
      this.appointmentStore.resetAvailableDates()
      this.appointmentStore.resetAvailableVehicles()
      this.appointmentStore.resetDisabledDates()
      this.appointmentStore.resetAvailableAdvisers()
      this.appointmentStore.resetSelectedDateHours()
      this.appointmentStore.resetSelectedDate()
      this.appointmentStore.resetSelectedAdviser()
      this.appointmentStore.resetSelectedVehicles()
      this.appointmentStore.resetSelectedHour()
      this.submissionFormStore.resetAppointmentData()
      if (this.previousStepRoute.length > 0) {
        return router.visit(this.previousStepRoute)
      }
    }
  }
})
</script>
